import {
	type UseMutationResult,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import { api } from "@/lib/api";
import { useRateLimitedMutation } from "@/lib/use-rate-limited-mutation";
import type {
	AssignSourceRequest,
	CreateMqttSourceRequest,
	CreateResellerRequest,
	ResellerDetail,
	ResellerManagerProfile,
	UpdateProfileRequest,
	UpdateResellerRequest,
	UploadFilterlistRequest,
	UploadMqttSourcesRequest,
	UploadMqttSourcesResponse,
	UploadRmLogoRequest,
} from "./types";

export function useCreateReseller() {
	const qc = useQueryClient();
	return useRateLimitedMutation(
		{
			mutationFn: async (data: CreateResellerRequest) => {
				const res = await api.post(
					"/api/reseller/manager/create_reseller/",
					data,
				);
				return res.data;
			},
			onSuccess: () => {
				qc.invalidateQueries({ queryKey: queryKeys.resellerManager.all });
			},
		},
		{ key: "reseller-manager:create-reseller", limit: 10, windowMs: 60 * 1000 },
	);
}

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 MB
const MAX_CSV_SIZE = 10 * 1024 * 1024; // 10 MB

export function useUploadMqttSources(): UseMutationResult<
	UploadMqttSourcesResponse,
	Error,
	UploadMqttSourcesRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ file }: UploadMqttSourcesRequest) => {
			if (file.size > MAX_CSV_SIZE) {
				throw new Error("Plik jest za duży. Maksymalny rozmiar to 10 MB.");
			}
			const formData = new FormData();
			formData.append("file", file);
			const res = await api.post<UploadMqttSourcesResponse>(
				"/api/reseller/manager/upload_mqttsources/",
				formData,
				{
					headers: { "Content-Type": "multipart/form-data" },
				},
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.resellerManager.mySources({}),
			});
		},
	});
}

export function useUploadFilterlist(): UseMutationResult<
	unknown,
	Error,
	UploadFilterlistRequest
> {
	return useMutation({
		mutationFn: async ({ mqtt_source_sn, file }: UploadFilterlistRequest) => {
			if (file.size > MAX_CSV_SIZE) {
				throw new Error("Plik jest za duży. Maksymalny rozmiar to 10 MB.");
			}
			const formData = new FormData();
			formData.append("mqtt_source_sn", mqtt_source_sn);
			formData.append("file", file);
			const res = await api.post(
				"/api/reseller/manager/upload_filterlist/",
				formData,
				{
					headers: { "Content-Type": "multipart/form-data" },
				},
			);
			return res.data;
		},
	});
}

export function useAssignSource(): UseMutationResult<
	unknown,
	Error,
	AssignSourceRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: AssignSourceRequest) => {
			const res = await api.post("/api/reseller/manager/assign_source/", data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.resellerManager.mySources({}),
			});
		},
	});
}

export function useCreateMqttSource(): UseMutationResult<
	unknown,
	Error,
	CreateMqttSourceRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: CreateMqttSourceRequest) => {
			const res = await api.post(
				"/api/reseller/manager/create_mqttsource/",
				data,
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.resellerManager.mySources({}),
			});
		},
	});
}

export function useUpdateRmProfile(): UseMutationResult<
	unknown,
	Error,
	UpdateProfileRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (data: UpdateProfileRequest) => {
			const res = await api.patch("/api/reseller/manager/my_profile/", data);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.resellerManager.myProfile() });
		},
	});
}

export function useUploadRmLogo(): UseMutationResult<
	ResellerManagerProfile,
	Error,
	UploadRmLogoRequest
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ logo }: UploadRmLogoRequest) => {
			if (logo.size > MAX_IMAGE_SIZE) {
				throw new Error("Logo jest za duże. Maksymalny rozmiar to 2 MB.");
			}
			const formData = new FormData();
			formData.append("logo", logo);
			const res = await api.patch<ResellerManagerProfile>(
				"/api/reseller/manager/my_profile/",
				formData,
				{ headers: { "Content-Type": "multipart/form-data" } },
			);
			return res.data;
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.resellerManager.myProfile() });
		},
	});
}

export function useUpdateReseller(): UseMutationResult<
	ResellerDetail,
	Error,
	{ id: number; data: UpdateResellerRequest }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, data }) => {
			const res = await api.patch<ResellerDetail>(
				`/api/reseller/manager/${id}/`,
				data,
			);
			return res.data;
		},
		onSuccess: (_data, { id }) => {
			qc.invalidateQueries({ queryKey: queryKeys.resellerManager.all });
			qc.invalidateQueries({
				queryKey: queryKeys.resellerManager.reseller(id),
			});
		},
	});
}

export function useDeleteReseller(): UseMutationResult<void, Error, number> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (id: number) => {
			await api.delete(`/api/reseller/manager/${id}/`);
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: queryKeys.resellerManager.all });
		},
	});
}

export function useDeleteMqttSource(): UseMutationResult<void, Error, string> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async (sn: string) => {
			await api.delete(`/api/reseller/manager/delete_mqttsource/${sn}/`);
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.resellerManager.mySources({}),
			});
		},
	});
}
